import {
	ActivityIndicator,
	BreadcrumbItem,
	Breadcrumbs,
	Button,
	Carousel,
	CarouselItem,
	Container,
	Heading,
	HorizontalRule,
	Link,
	MediaSection,
	MessageBar,
	Page,
	Picture,
	Section,
} from '@troon/ui';
import { createMemo, ErrorBoundary, For, Suspense, useContext } from 'solid-js';
import { createAsync } from '@solidjs/router';
import { Meta, Title } from '@solidjs/meta';
import { IconArrowRightMd } from '@troon/icons/arrow-right-md';
import { IconGiftCustom } from '@troon/icons/gift-custom';
import { IconMailOpenCustom } from '@troon/icons/mail-open-custom';
import { IconSelectMultipleCustom } from '@troon/icons/select-multiple-custom';
import { IconChevronDown } from '@troon/icons/chevron-down';
import { IconInfo } from '@troon/icons/info';
import { NoHydration } from 'solid-js/web';
import { SubscriptionProducts } from '../../../components/subscription-products';
import { AccessProductsCtx } from '../../../providers/card';
import { getConfigValue } from '../../../modules/config';
import { FrequentlyAskedQuestions } from '../../../components/faqs';
import { PlanComparison } from '../../../components/plan-comparison';
import { BenefitCard } from '../../../components/benefit';
import { ParticipationMap } from '../../../components/participation-map';
import { createContentfulRequest } from '../../content/[model]/_client';
import { TopoBg } from '../../../components/topo-bg';
import type { ApiResponse } from '@troon/api-client';
import type { RouteDefinition } from '@solidjs/router';
import type { ParentProps } from 'solid-js';

export default function GiftAccess() {
	const products = useContext(AccessProductsCtx);

	const benefitsData = createAsync(async () => getBenefits('access-landing-benefits'), { deferStream: true });

	const lowestPricedProduct = createMemo(() => {
		return products()?.reduce(
			(lowest, product) => (!lowest || product.totalAmount.value < lowest.totalAmount.value ? product : lowest),
			undefined as undefined | ApiResponse<'get', '/v0/troon-access-subscriptions/products/{id}'>,
		);
	});

	return (
		<>
			<Title>Gift Access Premium | Gift Exclusive Rates & Accelerated Earnings | Golf with Access</Title>
			<Meta
				name="description"
				content="Gift the golfer in your life with Access Premium. Exclusive rates, accelerated earnings, and year-round perks at 200+ top courses."
			/>
			<NoHydration>
				<TopoBg placement="top" class="absolute z-0 h-48 w-screen" />
			</NoHydration>
			<Container class="mt-4">
				<Breadcrumbs>
					<BreadcrumbItem href="/membership">Membership</BreadcrumbItem>
					<BreadcrumbItem href="/membership/gift">Gift Access Premium</BreadcrumbItem>
				</Breadcrumbs>
			</Container>
			<Page>
				<Container size="small" class="text-center text-balance">
					<Heading as="h1">
						<span class="block text-lg leading-12 font-normal text-neutral-700">Gifting ~</span> The perfect gift for
						the golfer in your life
					</Heading>
					<p class="text-lg">
						An Access Premium membership is the ultimate way to play Troon, with year-round savings on tee times at over
						200 world-class courses!
					</p>

					<Button as={Link} href="#products" class="mx-auto size-fit">
						Gift Access Premium <IconArrowRightMd />
					</Button>

					<span class="font-medium text-nowrap text-neutral-800">
						Starting at just <Suspense>{lowestPricedProduct()?.totalAmount.displayValue}</Suspense>
					</span>
				</Container>

				<Container size="small" class="overflow-hidden">
					<Picture
						src={`${getConfigValue('IMAGE_HOST')}/hero/premium-cards-shadow.png`}
						width={1188}
						height={907}
						sizes="(min-width: 768px) 40rem, 97vw"
						alt=""
						class="mx-auto max-w-fit"
					/>
				</Container>

				<Container>
					<HorizontalRule />
				</Container>

				<Container>
					<Section>
						<CenterContent>
							<Heading as="h2" size="h1">
								You gift. They save.
							</Heading>
							<p>Choose a Premium plan below to continue your gift purchase:</p>
						</CenterContent>
						<div class="grid w-full grid-cols-3 gap-16">
							<BenefitCallout>
								<IconSelectMultipleCustom class="size-16" />
								<Heading as="h3">Choose a Plan</Heading>
								<p>Select the Premium or Premium+ membership, perfectly tailored to any golfer’s needs.</p>
							</BenefitCallout>
							<BenefitCallout>
								<IconGiftCustom class="size-16" />
								<Heading as="h3">Personalize the Gift</Heading>
								<p>
									Add a message, schedule when you want to send it, and complete your purchase in just a few clicks.
								</p>
							</BenefitCallout>
							<BenefitCallout>
								<IconMailOpenCustom class="size-16" />
								<Heading as="h3">Sent via Email</Heading>
								<p>
									The gift recipient receives an email on the scheduled date with easy-to-follow instructions to redeem
									their gift.
								</p>
							</BenefitCallout>
						</div>
					</Section>
				</Container>

				<Container size="medium" id="products">
					<div class="z-30 grid grid-rows-2 gap-8 overflow-visible text-neutral-950 lg:grid-cols-2 lg:grid-rows-1">
						<SubscriptionProducts purchaseAs="gift" />
					</div>
				</Container>
				<Container>
					<details class="group flex flex-col gap-4">
						<Button as="summary" appearance="secondary" class="mx-auto size-fit">
							Compare all plans & Benefits <IconChevronDown class="transition-transform group-open:rotate-180" />
						</Button>
						<PlanComparison />
					</details>
				</Container>
				<Container>
					<Page>
						<Section>
							<Heading as="h2" class="text-center">
								Explore Participating Courses
							</Heading>
							<p class="mx-auto max-w-4xl text-center text-base text-balance md:text-lg">
								Explore the growing network of Access participating courses. Discover where you can book, earn points,
								and redeem rewards.
							</p>

							<ErrorBoundary fallback={null}>
								<Suspense
									fallback={
										<div class="flex h-[50svh] items-center justify-center md:h-[600px]">
											<ActivityIndicator />
										</div>
									}
								>
									<ParticipationMap />
								</Suspense>
							</ErrorBoundary>
						</Section>

						<HorizontalRule />

						<Section class="relative">
							<Heading as="h2">Additional Member Benefits</Heading>
							<p class="text-lg">Enjoy exclusive Access member benefits from your favorite brands.</p>

							<Suspense>
								<Carousel
									auto="play"
									description="Enjoy exclusive Access member benefits from your favorite brands."
									listClass="flex items-stretch -ms-4 xl:-ms-8"
									align="start"
									paginationClass="absolute -bottom-12 left-0 z-10"
									paginationButtonClass="text-neutral-400 aria-current:text-brand"
								>
									<For each={benefitsData()?.fields.benefits}>
										{(benefit) => {
											const { bannerImage, ...fields } = benefit!.fields;
											return (
												<CarouselItem class="min-w-0 basis-full ps-4 sm:basis-1/2 lg:basis-1/3 xl:basis-1/4 xl:ps-8">
													<BenefitCard {...fields} level="h3" size="h4" class="h-full" />
												</CarouselItem>
											);
										}}
									</For>
								</Carousel>
							</Suspense>
						</Section>

						<HorizontalRule />
						<MediaSection src={`${getConfigValue('IMAGE_HOST')}/hero/access.jpg`} reverse>
							<Heading as="h2">Guest Passes</Heading>
							<p>
								Guest Passes allow your playing partners to play at the same discounted rate as you for a given round.
								All Premium members receive 3 Guest Passes per/year with no restrictions on when they can be used.
							</p>
							<MessageBar appearance="brand" icon={IconInfo}>
								<p>
									<b>Need more than 3 guest passes?</b> Unlimited Guest Passes are available as an add-on to either
									Access tier for $75/mo (billed annually).
								</p>
							</MessageBar>
							<Button as={Link} href="/membership/guest-passes" appearance="secondary" class="size-fit grow-0">
								Learn more
							</Button>
						</MediaSection>

						<HorizontalRule />

						<Section>
							<Container size="medium">
								<Heading as="h2" size="h2" class="text-center">
									Frequently asked questions
								</Heading>
								<div class="overflow-hidden rounded-lg border border-neutral">
									<FrequentlyAskedQuestions id="access-premium-gifting-faqs" />
								</div>
							</Container>
						</Section>
					</Page>
				</Container>
			</Page>
		</>
	);
}

function CenterContent(props: ParentProps) {
	return <div class="relative z-0 mx-auto flex max-w-xl flex-col gap-4 text-center">{props.children}</div>;
}

function BenefitCallout(props: ParentProps) {
	return <div class="col-span-3 flex flex-col items-center gap-6 text-center lg:col-span-1">{props.children}</div>;
}

const getBenefits = createContentfulRequest('benefits');

export const route = { info: { nav: { hero: false } } } satisfies RouteDefinition;
